import { withListOverflow } from "../../../v2/components/ListOverflow.jsx";
export const LeaseAreasTable = withListOverflow(LeaseAreasListOwner);
// src/features/leases/components/LeaseAreasTable.jsx
//
// LSE-LEASE-TAB-AREAS-AND-SWITCHER — Areas sub-table within a lease summary.
// Renders rows from lease_areas with columns Name | Size | Commencement | Expiration.
// Rows with is_in_build = true carry a distinct "In Build" pill using the
// build-blue T token family (same as the In-Build stage badge).
//
// Renders even when there is only one area row (per brief).
// Returns null when areas is empty/missing.

import React from "react";
import { T } from "../../../app/tokens.js";
import { isSentinelEndDate, parseDateOnly } from "../leaseTabConfig.js";
import { leaseAreaPresentation } from "../leaseAreaPresentation.js";
import { AreaLifecycleBadge } from "./AreaLifecycleBadge.jsx";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmtAreaDate(d) {
  if (!d) return <span style={{ color: T.light }}>—</span>;
  if (isSentinelEndDate && isSentinelEndDate(d)) return "Ongoing";
  const dt = parseDateOnly ? parseDateOnly(d) : null;
  const parsed = dt || (typeof d === "string" ? new Date(d) : d);
  if (!parsed || isNaN(parsed.getTime())) return String(d);
  return parsed.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function fmtAreaSize(sizeSf) {
  if (sizeSf == null) return <span style={{ color: T.light }}>—</span>;
  if (sizeSf === 0) return <span style={{ color: T.mid }}>0 sf</span>;
  return `${Number(sizeSf).toLocaleString()} sf`;
}

// ─── In-Build pill ────────────────────────────────────────────────────────────

// ─── Component ────────────────────────────────────────────────────────────────

function LeaseAreasListOwner({ areas, asOf = new Date() }) {
  if (!Array.isArray(areas) || areas.length === 0) return null;
  const { groups } = leaseAreaPresentation({ areas }, asOf);

  const thStyle = {
    fontSize: T.fsXs,
    fontWeight: T.fwSemibold,
    color: T.mid,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    padding: "6px 10px 6px 0",
    textAlign: "left",
    borderBottom: `1px solid ${T.border}`,
    whiteSpace: "nowrap",
  };

  const tdStyle = (isLast) => ({
    fontSize: T.fsBase,
    color: T.dark,
    padding: isLast ? "8px 0" : "8px 10px 8px 0",
    borderBottom: `1px solid ${T.border}`,
    verticalAlign: "middle",
  });

  return (
    <div
      data-testid="lease-areas-table"
      style={{ marginTop: 8, overflowX: "auto" }}
    >
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Size</th>
            <th style={thStyle}>Est. Usable Sqft</th>
            <th style={thStyle}>Commencement</th>
            <th style={{ ...thStyle, padding: "6px 0" }}>Expiration</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => <React.Fragment key={group.status}>
            <tr><th colSpan={5} style={{ ...thStyle, color: T.light, paddingTop: 12 }}>{group.label} Areas</th></tr>
          {group.areas.map((area) => (
            <tr key={area.id} data-testid="lease-area-row">
              <td style={tdStyle(false)}>
                <span style={{ display: "inline-flex", alignItems: "center", color: area.status === "expired" ? T.light : T.dark }}>
                  {area.name}
                  <AreaLifecycleBadge area={area} />
                </span>
              </td>
              <td style={{ ...tdStyle(false), color: area.status === "expired" ? T.light : T.dark }}>{fmtAreaSize(area.sizeSf)}</td>
              <td style={tdStyle(false)}>{area.status === "expired" ? "—" : fmtAreaSize(area.usableSizeSf)}</td>
              <td style={tdStyle(false)}>{fmtAreaDate(area.commencementDate)}</td>
              <td style={{ ...tdStyle(true), padding: "8px 0" }}>{fmtAreaDate(area.expirationDate)}</td>
            </tr>
          ))}
          </React.Fragment>)}
        </tbody>
      </table>
    </div>
  );
}
